import analyticsService from '@lib/analytics/AnalyticsService'
import googleAdsService from '@lib/analytics/googleAds/GoogleAdsService'
import { useEffect, useRef, useState } from 'react'

/**
 * Check if pre-consent banner analytics tracking is enabled.
 * This controls whether `cookie_banner_shown` and `cookie_banner_abandoned` events
 * are pushed to GTM dataLayer before the user gives consent.
 *
 * Set NEXT_PUBLIC_ENABLE_PRECONSENT_BANNER_ANALYTICS=true in production only.
 * Disabled by default for stricter GDPR/ePrivacy compliance in non-prod environments.
 */
const isPreconsentTrackingEnabled = () =>
    process.env.NEXT_PUBLIC_ENABLE_PRECONSENT_BANNER_ANALYTICS === 'true'

export type CookieBot = {
    consent: {
        preferences: boolean
        statistics: boolean
        marketing: boolean
    }
    doNotTrack: boolean
    hasResponse: boolean
    hide(): void
    renew(): void
    runScripts(): void
    show(): void
    submitCustomConsent(
        optinPreferences?: boolean,
        optinStatistics?: boolean,
        optinMarketing?: boolean,
    ): void
}

export const useCookieConsent = () => {
    const [, setConsent] = useState<CookieBot['consent'] | undefined>()

    // Refs to track banner state and prevent duplicate events
    const bannerShownRef = useRef(false)
    const userInteractedRef = useRef(false)

    // Track banner shown event (pre-consent, gated by env variable)
    const handleBannerShown = () => {
        if (bannerShownRef.current) return // Prevent duplicate tracking
        bannerShownRef.current = true

        if (isPreconsentTrackingEnabled()) {
            window?.dataLayer?.push({
                event: 'cookie_banner_shown',
                timestamp: new Date().toISOString(),
            })
        }
    }

    // Track consent change (accept or decline)
    const handleConsentChange = async (action: 'accept' | 'decline') => {
        userInteractedRef.current = true
        const newConsent = { ...window.Cookiebot?.consent }
        setConsent(newConsent)

        const hasAnalyticsConsent =
            newConsent?.statistics === true &&
            newConsent?.marketing === true &&
            window?.Cookiebot?.doNotTrack === false

        // Push consent event to dataLayer - GTM will handle filtering based on consent
        window?.dataLayer?.push({
            event: 'cookie_consent_updated',
            consent: {
                statistics: newConsent?.statistics,
                marketing: newConsent?.marketing,
                preferences: newConsent?.preferences,
            },
            hasAnalyticsConsent,
            action,
        })

        // Process any pending analytics events if consent is granted
        if (hasAnalyticsConsent) {
            await analyticsService.processPendingEvents()
            await googleAdsService.processPendingConversions()
        }
    }

    // Track abandonment when user leaves page without interacting (pre-consent, gated by env variable)
    const handleBeforeUnload = () => {
        if (
            bannerShownRef.current &&
            !userInteractedRef.current &&
            isPreconsentTrackingEnabled()
        ) {
            window?.dataLayer?.push({
                event: 'cookie_banner_abandoned',
                timestamp: new Date().toISOString(),
            })
        }
    }

    useEffect(() => {
        // Banner display listener
        window.addEventListener('CookiebotOnDialogDisplay', handleBannerShown)

        // Consent change listeners
        const onAccept = () => handleConsentChange('accept')
        const onDecline = () => handleConsentChange('decline')

        window.addEventListener('CookiebotOnAccept', onAccept)
        window.addEventListener('CookiebotOnDecline', onDecline)

        // Abandonment tracking
        window.addEventListener('beforeunload', handleBeforeUnload)

        // If consent already exists on first load, call the callback to handle it
        // This handles the case where the event listeners are added after
        // the consent script has already been loaded
        if (window?.Cookiebot?.consent) {
            // User already made a choice in a previous session
            userInteractedRef.current = true
            handleConsentChange(
                window.Cookiebot.consent.statistics ? 'accept' : 'decline',
            )
        } else if (window?.Cookiebot && !window.Cookiebot.hasResponse) {
            // Banner is showing but no response yet
            handleBannerShown()
        }

        return () => {
            window.removeEventListener(
                'CookiebotOnDialogDisplay',
                handleBannerShown,
            )
            window.removeEventListener('CookiebotOnAccept', onAccept)
            window.removeEventListener('CookiebotOnDecline', onDecline)
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [])
}

// Utility function to check if user has given consent for analytics tracking
export const hasCookieConsent = () => {
    const consent = window.Cookiebot?.consent
    return (
        consent?.statistics === true &&
        consent?.marketing === true &&
        window.Cookiebot?.doNotTrack === false
    )
}
